import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch, formatDate, StatusPill } from "@/components/candidate/shared";

const STAGES = ["new", "under_review", "shortlisted", "interview", "selected", "hired"];
const STAGE_LABELS = {
    new: "Application Submitted",
    under_review: "Under Review",
    shortlisted: "Shortlisted",
    interview: "Interview",
    selected: "Selected",
    hired: "Hired",
};

const Timeline = ({ status }) => {
    if (status === "rejected" || status === "withdrawn") {
        return (
            <div data-testid="application-timeline" className="border-l-2 border-ink/15 pl-6">
                <div className="flex items-center gap-3 py-2">
                    <CheckCircle2 size={18} strokeWidth={1.5} className="text-forest" />
                    <span className="text-sm text-ink/70">Application Submitted</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                    <span className="flex h-[18px] w-[18px] items-center justify-center border border-ink/30 text-[10px] text-ink/50">✕</span>
                    <span className="text-sm font-medium text-ink/70">
                        {status === "rejected" ? "Not selected for this role" : "Application withdrawn"}
                    </span>
                </div>
            </div>
        );
    }
    const currentIndex = STAGES.indexOf(status);
    return (
        <ol data-testid="application-timeline" className="border-l-2 border-ink/15 pl-6">
            {STAGES.map((stage, i) => {
                const reached = i <= currentIndex;
                const current = i === currentIndex;
                return (
                    <li key={stage} className="flex items-center gap-3 py-2.5">
                        {reached ? (
                            <CheckCircle2 size={18} strokeWidth={1.5} className={current ? "text-forest" : "text-forest/50"} />
                        ) : (
                            <span className="h-[18px] w-[18px] rounded-full border border-ink/20" />
                        )}
                        <span className={`text-sm ${current ? "font-semibold text-ink" : reached ? "text-ink/60" : "text-ink/35"}`}>
                            {STAGE_LABELS[stage]}
                        </span>
                        {current && (
                            <span className="border border-champagne/60 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8a7320]">
                                Current
                            </span>
                        )}
                    </li>
                );
            })}
        </ol>
    );
};

const Row = ({ label, value }) =>
    value ? (
        <div className="grid grid-cols-[140px_1fr] gap-4 border-b border-ink/10 py-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">{label}</span>
            <span className="break-words text-sm text-ink/80">{value}</span>
        </div>
    ) : null;

export default function CandidateApplicationDetail() {
    const { id } = useParams();
    const [app, setApp] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        apiFetch(`/candidate/applications/${id}`)
            .then((r) => {
                if (!r.ok) {
                    setMissing(true);
                    return null;
                }
                return r.json();
            })
            .then((d) => d && setApp(d));
    }, [id]);

    if (missing) {
        return (
            <AccountLayout title="Application not found" subtitle="This application doesn't exist or doesn't belong to your account.">
                <a href="/account/applications" data-testid="app-detail-back" className="text-sm font-medium text-forest">
                    ← Back to my applications
                </a>
            </AccountLayout>
        );
    }

    return (
        <AccountLayout title={app ? app.job_title : "Loading…"} subtitle={app ? `${app.job_company}${app.job_country ? ` · ${app.job_country}` : ""}` : ""}>
            {app && (
                <div className="grid gap-12 lg:grid-cols-12" data-testid="application-detail-page">
                    <div className="lg:col-span-5">
                        <div className="border border-ink/10 bg-sand p-7">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
                                        Reference
                                    </p>
                                    <p className="mt-1 font-serif text-xl font-semibold tracking-[0.08em] text-forest">
                                        {app.reference}
                                    </p>
                                </div>
                                <StatusPill status={app.status} />
                            </div>
                            <div className="mt-6 border-t border-ink/10 pt-5 text-sm text-ink/60">
                                <p>Applied {formatDate(app.created_at)}</p>
                                <p className="mt-1">Last updated {formatDate(app.updated_at)}</p>
                            </div>
                            {app.job_slug && (
                                <a
                                    href={`/jobs/${app.job_slug}`}
                                    data-testid="app-view-job-link"
                                    className="mt-6 inline-flex items-center gap-2 border border-forest/30 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory"
                                >
                                    View job posting
                                    <ArrowUpRight size={14} strokeWidth={1.5} />
                                </a>
                            )}
                        </div>

                        <h2 className="mt-10 font-serif text-xl font-medium tracking-tight">Your progress</h2>
                        <div className="mt-5">
                            <Timeline status={app.status} />
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-xl font-medium tracking-tight">Submitted information</h2>
                        <div className="mt-5 border-t border-ink/10">
                            <Row label="Full name" value={app.full_name} />
                            <Row label="Email" value={app.email} />
                            <Row label="Phone / WhatsApp" value={app.phone} />
                            <Row label="Country of residence" value={app.country_of_residence} />
                            <Row label="Nationality" value={app.nationality} />
                            <Row label="Profession" value={app.profession} />
                            <Row label="Experience" value={app.years_experience} />
                            <Row label="Qualification" value={app.highest_qualification} />
                            <Row label="Certifications" value={app.certifications} />
                            <Row label="Preferred destination" value={app.preferred_destination} />
                            <Row label="CV" value={app.cv?.original} />
                        </div>
                        {app.cover_letter && (
                            <div className="mt-8">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Cover message</p>
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink/70">{app.cover_letter}</p>
                            </div>
                        )}
                        <p className="mt-8 text-xs leading-relaxed text-ink/45">
                            You'll receive an email whenever your application status changes. Questions?
                            Contact us on WhatsApp +234 814 063 5506 or at contact@afrinexaglobal.org.
                        </p>
                    </div>
                </div>
            )}
        </AccountLayout>
    );
}
